// Grilles d'inventaire : icône de chaque objet à sa place, cases « ? » pendant la fouille, glisser-déposer entre grilles,
// fiche détaillée au survol.
import { fits, gridValue, maxDur, modsValue, type Grid, type ItemInst, type Placed } from '../shared/inventory';
import { ITEMS } from '../shared/items';
import type { Loadout, Place } from '../shared/protocol';
import { MAP } from '../shared/map';
import { MODS, SLOT_NAMES, WEAPONS, weaponStats } from '../shared/weapons';
import { iconFor } from './icons';

// Taille d'une case : plus grande sur les grands écrans (fixée au chargement)
export const CELL = innerWidth >= 1600 ? 58 : innerWidth >= 1360 ? 52 : 46;
const esc = (s: string) => s.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
const cr = (n: number) => `${n.toLocaleString('fr-FR')} cr`;

// Où se trouve une grille : un emplacement du joueur, ou le conteneur fouillé
export type Where = Place | 'cont' | 'sell';

export interface GridHandlers {
  click?: (it: ItemInst, e: MouseEvent) => void;
  context?: (it: ItemInst, e: MouseEvent) => void;
}
export type DropHandler = (it: ItemInst, from: Where, to: Where, x: number, y: number) => void;
let onDrop: DropHandler = () => {};
export const setDropHandler = (h: DropHandler) => { onDrop = h; };

interface Registered { grid: Grid | null; where: Where; slot?: boolean; zone?: boolean; items: Map<number, ItemInst>; h: GridHandlers }
const registry = new WeakMap<HTMLElement, Registered>();

function itemHtml(it: ItemInst, w: number, h: number) {
  const d = ITEMS[it.id]!;
  const qty = d.weapon ? `${it.ammo ?? 0}/${weaponStats(d.weapon, it.mods).magazine}` : d.armor ? `${Math.round(it.dur ?? 0)}/${Math.round(maxDur(it))}` : d.pack ? `${d.pack.w}×${d.pack.h}` : d.key ? `${it.uses ?? d.key.uses}/${d.key.uses}` : it.n > 1 ? `${it.n}` : '';
  const icon = iconFor(it);
  const nMods = Object.keys(it.mods ?? {}).length;
  return `${nMods ? `<span class="md" title="${nMods} accessoire${nMods > 1 ? 's' : ''}">+${nMods}</span>` : ''}${icon ? `<img src="${icon}" alt="" draggable="false">` : ''}<span class="nm">${esc(d.short)}</span>${qty ? `<span class="qt">${qty}</span>` : ''}${it.free ? '<span class="fr">kit</span>' : ''}`
    + `<i class="sz" style="--w:${w};--h:${h}"></i>`;
}

export function renderGrid(el: HTMLElement, g: Grid, where: Where, h: GridHandlers, ghosts?: { slots: [number, number, number, number][]; next: number }) {
  el.classList.add('grid');
  el.dataset.where = where;
  el.style.width = `${g.w * CELL}px`;
  el.style.height = `${g.h * CELL}px`;
  el.style.setProperty('--cell', `${CELL}px`);
  el.innerHTML = g.items.map((it: Placed) => {
    const d = ITEMS[it.id]!;
    return `<div class="it ${d.cat}" data-uid="${it.uid}" style="left:${it.x * CELL}px;top:${it.y * CELL}px;width:${d.w * CELL - 2}px;height:${d.h * CELL - 2}px">${itemHtml(it, d.w, d.h)}</div>`;
  }).join('') + (ghosts?.slots ?? []).map(([x, y, w, hh], i) =>
    `<div class="it ghost${i === 0 ? ' searching' : ''}" style="left:${x * CELL}px;top:${y * CELL}px;width:${w * CELL - 2}px;height:${hh * CELL - 2}px${i === 0 ? `;--t:${ghosts!.next}s` : ''}"><span>?</span></div>`).join('');
  registry.set(el, { grid: g, where, items: new Map(g.items.map(i => [i.uid, i])), h });
  bind(el);
}

// Emplacement d'arme (principale ou pistolet)
export function renderSlot(el: HTMLElement, it: ItemInst | null, where: 'primary' | 'secondary' | 'helmet' | 'vest' | 'backpack', caption: string, h: GridHandlers, active = false) {
  el.classList.add('slot');
  el.classList.toggle('active', active);
  el.dataset.where = where;
  el.innerHTML = `<span class="cap">${caption}</span>${it ? `<div class="it weapon" data-uid="${it.uid}">${itemHtml(it, 4, 2)}</div>` : '<span class="empty">vide</span>'}`;
  registry.set(el, { grid: null, where, slot: true, items: new Map(it ? [[it.uid, it]] : []), h });
  bind(el);
}

// Zone où l'on peut lâcher un objet (par exemple pour le vendre)
export function registerZone(el: HTMLElement, where: Where) {
  el.dataset.where = where;
  registry.set(el, { grid: null, where, zone: true, items: new Map(), h: {} });
}

// ---------------------------------------------------------------- Fiche au survol

const tip = document.createElement('div');
tip.id = 'itemTip';
document.body.append(tip);
// Équipement actuel (planque ou raid) : l'infobulle compare l'objet survolé à celui porté au même emplacement
let currentLoadout: () => Loadout | null | undefined = () => null;
export function setLoadoutSource(f: () => Loadout | null | undefined) { currentLoadout = f; }
function equippedFor(it: ItemInst): ItemInst | null {
  const l = currentLoadout(), d = ITEMS[it.id]!;
  if (!l) return null;
  const worn = d.weapon ? (d.weapon === 'pistol' ? l.secondary : l.primary) : d.armor ? l[d.armor.slot] : d.pack ? l.backpack : null;
  return worn && worn.uid !== it.uid ? worn : null;
}
function compareRows(it: ItemInst, other: ItemInst): string {
  const a = ITEMS[it.id]!, b = ITEMS[other.id]!;
  const rows: [string, number, number, string?][] = [];
  if (a.weapon && b.weapon) {
    const wa = weaponStats(a.weapon, it.mods), wb = weaponStats(b.weapon, other.mods);
    rows.push(['Dégâts', Math.round(wa.damage * (wa.pellets ?? 1)), Math.round(wb.damage * (wb.pellets ?? 1))], ['Cadence', wa.rpm, wb.rpm], ['Chargeur', wa.magazine, wb.magazine], ['Portée', Math.round(wa.range), Math.round(wb.range), ' m'], ['Rechargement', -Math.round(wa.reload * 10) / 10, -Math.round(wb.reload * 10) / 10, ' s'], ['Stabilité', Math.round(100 / wa.recoilMul), Math.round(100 / wb.recoilMul)]);
  }
  if (a.armor && b.armor) rows.push(['Classe', a.armor.cls, b.armor.cls], ['Durabilité', Math.round(it.dur ?? a.armor.dur), Math.round(other.dur ?? b.armor.dur)]);
  if (a.pack && b.pack) rows.push(['Cases', a.pack.w * a.pack.h, b.pack.w * b.pack.h]);
  if (!rows.length) return '';
  const fmt = (v: number) => (Number.isInteger(v) ? String(Math.abs(v)) : Math.abs(v).toFixed(1).replace('.', ','));
  const cells = rows.map(([k, va, vb, unit = '']) => {
    const diff = va - vb, cls = diff > 0 ? 'up' : diff < 0 ? 'down' : 'eq';
    const shown = k === 'Rechargement' ? -diff : diff; // plus court = mieux
    return `<dt>${k}</dt><dd class="${cls}">${diff ? `${shown > 0 ? '+' : '−'}${fmt(shown)}${unit}` : '='}</dd>`;
  }).join('');
  return `<div class="cmp"><h5>Comparé à : ${esc(b.short)} <small>(porté)</small></h5><dl>${cells}</dl></div>`;
}

function showTip(it: ItemInst, x: number, y: number) {
  const d = ITEMS[it.id]!;
  const rows: string[] = [];
  if (d.weapon) {
    const w = weaponStats(d.weapon, it.mods);
    rows.push(`<dt>Dégâts</dt><dd>${Math.round(w.damage)}${w.pellets ? ` × ${w.pellets}` : ''}</dd>`, `<dt>Cadence</dt><dd>${w.rpm} coups/min</dd>`,
      `<dt>Chargeur</dt><dd>${it.ammo ?? 0} / ${w.magazine}</dd>`, `<dt>Tir</dt><dd>${w.auto ? 'automatique' : 'semi-automatique'}</dd>`, `<dt>Portée</dt><dd>${Math.round(w.range)} m</dd>`,
      `<dt>Recul</dt><dd>${Math.round(w.recoilMul * 100)} %</dd>`);
    const mods = Object.entries(it.mods ?? {}).filter(([, m]) => m);
    if (mods.length) rows.push(`<dt>Accessoires</dt><dd>${mods.map(([s, m]) => `${SLOT_NAMES[s as keyof typeof SLOT_NAMES]} : ${esc(ITEMS[m!]!.short)}`).join('<br>')}</dd>`);
    if (!it.free) rows.push('<dt>Personnaliser</dt><dd>Ctrl + clic</dd>');
  }
  if (d.mod) {
    const m = MODS[d.mod]!, fx: string[] = [];
    const pct = (k: string, v: number | undefined, good: 'low' | 'high') => { if (v === undefined || v === 1) return; const p = Math.round((v - 1) * 100); fx.push(`<span class="${(p < 0) === (good === 'low') ? 'up' : 'down'}">${k} ${p > 0 ? '+' : '−'}${Math.abs(p)} %</span>`); };
    pct('Recul', m.recoil, 'low'); pct('Dispersion en mouvement', m.move, 'low'); pct('Chargeur', m.mag, 'high'); pct('Rechargement', m.reload, 'low');
    pct('Bruit', m.noise, 'low'); pct('Dégâts', m.damage, 'high'); pct('Portée', m.range, 'high'); pct('Visée', m.reach, 'high');
    rows.push(`<dt>Emplacement</dt><dd>${SLOT_NAMES[m.slot]}</dd>`, `<dt>Effets</dt><dd class="fx">${fx.join('<br>')}</dd>`,
      `<dt>Armes</dt><dd>${(m.only ?? (Object.keys(WEAPONS) as (keyof typeof WEAPONS)[]).filter(w => WEAPONS[w].slots.includes(m.slot))).map(w => WEAPONS[w].short).join(', ')}</dd>`);
  }
  if (d.key) rows.push(`<dt>Ouvre</dt><dd>${esc(MAP.locks.find(l => l.id === d.key!.lock)?.name ?? '—')}</dd>`, `<dt>Utilisations</dt><dd>${it.uses ?? d.key.uses} / ${d.key.uses}</dd>`);
  if (d.heal) rows.push(`<dt>Soin</dt><dd>${d.heal} PV en ${d.useTime} s</dd>`);
  if (d.stopBleed) rows.push('<dt>Saignement</dt><dd>arrête</dd>');
  if (d.splint) rows.push('<dt>Fracture</dt><dd>soigne</dd>');
  if (d.armor) rows.push(`<dt>Protection</dt><dd>${d.armor.slot === 'helmet' ? 'tête' : 'torse'}, classe ${d.armor.cls}</dd>`, `<dt>Durabilité</dt><dd>${Math.round(it.dur ?? 0)} / ${Math.round(maxDur(it))}${maxDur(it) < d.armor.dur ? ` <small>(neuf : ${d.armor.dur})</small>` : ''}${(it.dur ?? 0) < maxDur(it) ? ' · Ctrl + clic : réparer' : ''}</dd>`);
  if (d.pen) rows.push(`<dt>Perforation</dt><dd>${d.pen} (traverse la classe ${Math.max(0, Math.floor(d.pen / 10))})</dd>`);
  if (d.pack) rows.push(`<dt>Capacité</dt><dd>${d.pack.w} × ${d.pack.h} cases</dd>`, `<dt>Contenu</dt><dd>${it.grid?.items.length ? `${it.grid.items.length} objet${it.grid.items.length > 1 ? 's' : ''}` : 'vide'}</dd>`);
  if (d.stack > 1) rows.push(`<dt>Quantité</dt><dd>${it.n} / ${d.stack}</dd>`);
  rows.push(`<dt>Taille</dt><dd>${d.w} × ${d.h}</dd>`, `<dt>Valeur</dt><dd>${it.free ? 'aucune (kit civil)' : cr(d.value * it.n + modsValue(it) + (it.grid ? gridValue(it.grid) : 0))}</dd>`);
  const icon = iconFor(it);
  const worn = equippedFor(it);
  tip.innerHTML = `${icon ? `<img src="${icon}" alt="">` : ''}<h4>${esc(d.name)}</h4><dl>${rows.join('')}</dl>${worn ? compareRows(it, worn) : ''}`;
  tip.className = `show ${d.cat}`;
  const r = tip.getBoundingClientRect();
  tip.style.transform = `translate(${Math.min(x + 18, innerWidth - r.width - 8)}px, ${Math.min(y + 18, innerHeight - r.height - 8)}px)`;
}
const hideTip = () => { tip.className = ''; };

// ---------------------------------------------------------------- Glisser-déposer

let drag: { it: ItemInst; from: Where; startX: number; startY: number; grabX: number; grabY: number; float: HTMLElement | null; src: HTMLElement } | null = null;
const marker = document.createElement('div');
marker.className = 'dropMark';

function bind(el: HTMLElement) {
  el.onpointerdown = e => {
    if (e.button !== 0) return;
    const t = (e.target as HTMLElement).closest('[data-uid]') as HTMLElement | null;
    const reg = registry.get(el);
    const it = t && reg?.items.get(Number(t.dataset.uid));
    if (!t || !it || !reg) return;
    const r = t.getBoundingClientRect();
    drag = { it, from: reg.where, startX: e.clientX, startY: e.clientY, grabX: e.clientX - r.left, grabY: e.clientY - r.top, float: null, src: t };
    e.preventDefault();
  };
  el.oncontextmenu = e => {
    e.preventDefault();
    const t = (e.target as HTMLElement).closest('[data-uid]') as HTMLElement | null;
    const reg = registry.get(el);
    const it = t && reg?.items.get(Number(t.dataset.uid));
    if (it && reg?.h.context) reg.h.context(it, e);
  };
  el.onpointermove = e => {
    if (drag) return;
    const t = (e.target as HTMLElement).closest('[data-uid]') as HTMLElement | null;
    const it = t && registry.get(el)?.items.get(Number(t.dataset.uid));
    if (it) showTip(it, e.clientX, e.clientY); else hideTip();
  };
  el.onpointerleave = hideTip;
}

// Grille ou emplacement sous le pointeur, et case visée (en tenant compte de l'endroit où l'objet a été saisi)
function dropTarget(x: number, y: number) {
  const under = document.elementFromPoint(x, y)?.closest('[data-where]') as HTMLElement | null;
  if (!under || !drag) return null;
  const reg = registry.get(under);
  if (!reg) return null;
  const r = under.getBoundingClientRect();
  const cx = Math.round((x - drag.grabX - r.left) / CELL), cy = Math.round((y - drag.grabY - r.top) / CELL);
  const d = ITEMS[drag.it.id]!;
  let ok = false;
  if (reg.zone) ok = drag.from !== 'cont';
  else if (reg.slot) ok = reg.where === 'backpack' ? d.cat === 'bag' : reg.where === 'helmet' || reg.where === 'vest' ? d.armor?.slot === reg.where : d.cat === 'weapon' && (reg.where === 'secondary') === (d.weapon === 'pistol');
  else if (reg.grid) {
    const same = reg.grid.items.find(i => i.uid === drag!.it.uid);
    ok = fits(reg.grid, cx, cy, d.w, d.h, same?.uid);
    // Déposer sur une pile du même objet : on complète la pile
    if (!ok) { const onto = reg.grid.items.find(i => i.id === d.id && i.uid !== drag!.it.uid && cx >= i.x && cx < i.x + d.w && cy >= i.y && cy < i.y + d.h); ok = !!onto && onto.n < d.stack; }
  }
  return { el: under, reg, cx, cy, ok, r };
}

addEventListener('pointermove', e => {
  if (!drag) return;
  if (!drag.float) {
    if (Math.hypot(e.clientX - drag.startX, e.clientY - drag.startY) < 5) return;
    hideTip();
    drag.float = drag.src.cloneNode(true) as HTMLElement;
    drag.float.classList.add('dragging');
    drag.float.style.left = drag.float.style.top = '0';
    document.body.append(drag.float);
    drag.src.classList.add('lifted');
  }
  drag.float.style.transform = `translate(${e.clientX - drag.grabX}px, ${e.clientY - drag.grabY}px)`;
  const t = dropTarget(e.clientX, e.clientY);
  if (t && t.reg.grid) {
    const d = ITEMS[drag.it.id]!;
    t.el.append(marker);
    Object.assign(marker.style, { left: `${t.cx * CELL}px`, top: `${t.cy * CELL}px`, width: `${d.w * CELL}px`, height: `${d.h * CELL}px` });
    marker.classList.toggle('bad', !t.ok);
  } else marker.remove();
  document.querySelectorAll('.hover[data-where]').forEach(s => s.classList.remove('hover', 'bad'));
  if (t && (t.reg.slot || t.reg.zone)) t.el.classList.add('hover', ...(t.ok ? [] : ['bad']));
});

addEventListener('pointerup', e => {
  if (!drag) return;
  const d = drag;
  const t = d.float ? dropTarget(e.clientX, e.clientY) : null; // avant de lâcher l'objet : la cible dépend de lui
  drag = null;
  marker.remove();
  document.querySelectorAll('.hover[data-where]').forEach(s => s.classList.remove('hover', 'bad'));
  d.src.classList.remove('lifted');
  if (!d.float) {
    // Simple clic
    const reg = [...document.querySelectorAll('[data-where]')].map(n => registry.get(n as HTMLElement)).find(r => r?.items.get(d.it.uid) === d.it);
    reg?.h.click?.(d.it, e);
    return;
  }
  d.float.remove();
  if (t && t.ok) onDrop(d.it, d.from, t.reg.where, t.cx, t.cy);
});
